import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Paper,
    Typography,
    useTheme,
    alpha,
    Chip,
    LinearProgress,
    Stack,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    PriorityHigh,
    Flag,
    Schedule,
    Person,
    CheckCircle,
    RadioButtonUnchecked,
    PlayArrow,
    Pause,
    ExpandMore,
    ExpandLess,
    ChevronLeft,
    ChevronRight,
    TouchApp,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import { selectCurrentUser } from "store/features/auth/authSelectors";

const priorityConfig = {
    urgent: {
        color: "#ef4444",
        bg: alpha("#ef4444", 0.12),
        label: "Urgent",
        icon: <PriorityHigh sx={{ fontSize: 15 }} />,
    },
    high: {
        color: "#f59e0b",
        bg: alpha("#f59e0b", 0.12),
        label: "High",
        icon: <Flag sx={{ fontSize: 15 }} />,
    },
    medium: {
        color: "#3b82f6",
        bg: alpha("#3b82f6", 0.12),
        label: "Medium",
        icon: <Schedule sx={{ fontSize: 15 }} />,
    },
    low: {
        color: "#8b5cf6",
        bg: alpha("#8b5cf6", 0.12),
        label: "Low",
        icon: <Flag sx={{ fontSize: 15 }} />,
    },
};

const statusConfig = {
    todo: { color: "#94a3b8", label: "To Do" },
    in_progress: { color: "#3b82f6", label: "In Progress" },
    review: { color: "#f59e0b", label: "Review" },
    done: { color: "#10b981", label: "Done" },
};

// Helper to determine if a task is assigned to / created by current user
const isMyTask = (task, userId, employeeId, userName) => {
    if (!userId && !employeeId && !userName) return true;

    // Check task.assigned_to
    if (task.assigned_to !== undefined && task.assigned_to !== null) {
        if (Array.isArray(task.assigned_to)) {
            if (
                task.assigned_to.some(
                    (id) =>
                        String(id) === String(userId) ||
                        String(id) === String(employeeId)
                )
            ) {
                return true;
            }
        } else if (typeof task.assigned_to === "object") {
            if (
                String(task.assigned_to.id) === String(userId) ||
                String(task.assigned_to.id) === String(employeeId) ||
                (task.assigned_to.name &&
                    userName &&
                    task.assigned_to.name.toLowerCase() === userName.toLowerCase())
            ) {
                return true;
            }
        } else {
            const assignedStr = String(task.assigned_to);
            if (assignedStr.includes(",")) {
                const ids = assignedStr.split(",").map((s) => s.trim());
                if (
                    ids.some(
                        (id) =>
                            id === String(userId) ||
                            id === String(employeeId) ||
                            (userName && id.toLowerCase() === userName.toLowerCase())
                    )
                ) {
                    return true;
                }
            } else if (
                assignedStr === String(userId) ||
                assignedStr === String(employeeId) ||
                (userName && assignedStr.toLowerCase() === userName.toLowerCase())
            ) {
                return true;
            }
        }
    }

    // Check task.assignees array
    if (Array.isArray(task.assignees) && task.assignees.length > 0) {
        if (
            task.assignees.some(
                (a) =>
                    String(a.employee_id || a.user_id || a.id) === String(userId) ||
                    String(a.employee_id || a.user_id || a.id) === String(employeeId)
            )
        ) {
            return true;
        }
    }

    // Check created_by or user_id
    if (
        task.created_by &&
        (String(task.created_by) === String(userId) ||
            String(task.created_by) === String(employeeId))
    ) {
        return true;
    }
    if (
        task.user_id &&
        (String(task.user_id) === String(userId) ||
            String(task.user_id) === String(employeeId))
    ) {
        return true;
    }

    return false;
};

const TopPriorityTasks = ({ tasks = [], userRole: propUserRole }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const currentUser = useSelector(selectCurrentUser);
    const userRaw = getSecureLocalItem("user");
    const storedUser = userRaw ? (typeof userRaw === "string" ? JSON.parse(userRaw) : userRaw) : null;
    const user = storedUser || currentUser;

    const userId = user?.id || user?.user_id;
    const employeeId = user?.employee_id || user?.id;
    const userName = user?.name || (user?.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : null);

    const activeRole = (
        propUserRole ||
        getSecureLocalItem("role") ||
        currentUser?.role ||
        currentUser?.role_name ||
        "Company"
    ).toString().toLowerCase();

    const isCompanyRole =
        activeRole === "company" ||
        activeRole === "admin" ||
        activeRole === "super_admin" ||
        activeRole === "companyadmin";

    const [activeIndex, setActiveIndex] = useState(0);
    const [expandedTask, setExpandedTask] = useState(null);
    const [direction, setDirection] = useState(1);

    // Filter and sort top priority tasks (max 10)
    const priorityTasks = useMemo(() => {
        if (!tasks || !Array.isArray(tasks)) return [];

        const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };

        // Filter individual tasks (exclude parent tasks)
        let list = tasks.filter(
            (task) => task.is_parent_task === 0 || task.is_parent_task === null || task.is_parent_task === undefined
        );

        // If not company role, filter to show only "my tasks"
        if (!isCompanyRole) {
            list = list.filter((task) => isMyTask(task, userId, employeeId, userName));
        }

        // Sort by due date (soonest first) then priority (urgent first)
        list.sort((a, b) => {
            if (a.due_date && b.due_date) {
                const dateA = new Date(a.due_date).getTime();
                const dateB = new Date(b.due_date).getTime();
                if (dateA !== dateB) return dateA - dateB;
            } else if (a.due_date && !b.due_date) {
                return -1;
            } else if (!a.due_date && b.due_date) {
                return 1;
            }

            const pA = priorityOrder[a.priority?.toLowerCase()] ?? 99;
            const pB = priorityOrder[b.priority?.toLowerCase()] ?? 99;
            return pA - pB;
        });

        // Maximum 10 cards
        return list.slice(0, 10);
    }, [tasks, isCompanyRole, userId, employeeId, userName]);

    // Keep activeIndex within bounds
    useEffect(() => {
        if (activeIndex >= priorityTasks.length && priorityTasks.length > 0) {
            setActiveIndex(0);
        }
    }, [priorityTasks.length, activeIndex]);

    const handleNext = (e) => {
        if (e) e.stopPropagation();
        if (priorityTasks.length <= 1) return;
        setDirection(1);
        setActiveIndex((prev) => (prev + 1) % priorityTasks.length);
        setExpandedTask(null);
    };

    const handlePrev = (e) => {
        if (e) e.stopPropagation();
        if (priorityTasks.length <= 1) return;
        setDirection(-1);
        setActiveIndex((prev) => (prev - 1 + priorityTasks.length) % priorityTasks.length);
        setExpandedTask(null);
    };

    const handleCardClick = () => {
        if (priorityTasks.length > 1) {
            handleNext();
        }
    };

    const toggleExpand = (e, taskId) => {
        e.stopPropagation();
        setExpandedTask((prev) => (prev === taskId ? null : taskId));
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "done":
                return <CheckCircle sx={{ fontSize: 14, color: "#10b981" }} />;
            case "in_progress":
                return <PlayArrow sx={{ fontSize: 14, color: "#3b82f6" }} />;
            case "review":
                return <Pause sx={{ fontSize: 14, color: "primary.main" }} />;
            default:
                return <RadioButtonUnchecked sx={{ fontSize: 14, color: "#94a3b8" }} />;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "No due date";
        const date = new Date(dateString);
        const today = new Date();
        const diffTime = date - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
        if (diffDays === 0) return "Due today";
        if (diffDays === 1) return "Due tomorrow";
        return `Due in ${diffDays} days`;
    };

    const getTimeSpent = (actualHours) => {
        if (!actualHours || actualHours === "0.00") return "Not started";
        const hours = parseFloat(actualHours);
        if (hours < 1) return `${Math.round(hours * 60)} min`;
        return `${hours.toFixed(1)} hrs`;
    };

    if (!priorityTasks || priorityTasks.length === 0) {
        return (
            <Paper
                variant="outlined"
                sx={{
                    py: 6,
                    px: 3,
                    textAlign: "center",
                    borderRadius: 3,
                    borderColor: isDark ? "#1e293b" : "#e2e8f0",
                    background: isDark ? alpha("#111827", 0.4) : "#ffffff",
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {isCompanyRole
                        ? "No priority tasks available across projects"
                        : "No priority tasks assigned to you"}
                </Typography>
            </Paper>
        );
    }

    const currentTask = priorityTasks[activeIndex];
    const totalCards = priorityTasks.length;

    // Determine stacked background cards preview (up to 2 layers behind)
    const backgroundCards = [];
    if (totalCards > 1) {
        backgroundCards.push({
            task: priorityTasks[(activeIndex + 1) % totalCards],
            offsetY: 12,
            scale: 0.95,
            opacity: 0.7,
            zIndex: 2,
        });
    }
    if (totalCards > 2) {
        backgroundCards.push({
            task: priorityTasks[(activeIndex + 2) % totalCards],
            offsetY: 24,
            scale: 0.9,
            opacity: 0.4,
            zIndex: 1,
        });
    }

    // Framer Motion variants for active card exit/enter transition
    const cardVariants = {
        initial: (dir) => ({
            x: dir > 0 ? 120 : -120,
            opacity: 0,
            scale: 0.92,
            rotate: dir > 0 ? 4 : -4,
        }),
        animate: {
            x: 0,
            opacity: 1,
            scale: 1,
            rotate: 0,
            transition: {
                type: "spring",
                stiffness: 350,
                damping: 28,
            },
        },
        exit: (dir) => ({
            x: dir > 0 ? -120 : 120,
            opacity: 0,
            scale: 0.92,
            rotate: dir > 0 ? -4 : 4,
            transition: { duration: 0.2 },
        }),
    };

    const priority = priorityConfig[currentTask.priority?.toLowerCase()] || priorityConfig.medium;
    const status = statusConfig[currentTask.status?.toLowerCase()] || statusConfig.todo;
    const isExpanded = expandedTask === currentTask.id;

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
            {/* Header Controls & Counter */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justify: "space-between",
                    mb: 1.5,
                    px: 0.5,
                    width: "100%",
                }}
            >
                {/* <Stack direction="row" spacing={1} alignItems="center"> */}
                <Chip
                    label={`Task ${activeIndex + 1} of ${totalCards}`}
                    size="small"
                    sx={{
                        mr: 1,
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        height: 24,
                        bgcolor: isDark ? alpha("#3b82f6", 0.2) : alpha("#3b82f6", 0.1),
                        color: isDark ? "#60a5fa" : "#2563eb",
                        border: `1px solid ${isDark ? alpha("#3b82f6", 0.3) : alpha("#3b82f6", 0.2)}`,
                    }}
                />
                {/* {totalCards > 1 && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <TouchApp sx={{ fontSize: 14, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Click card for next
                            </Typography>
                        </Stack>
                    )} */}
                {/* </Stack> */}

                {totalCards > 1 && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <IconButton
                            size="small"
                            onClick={handlePrev}
                            sx={{
                                width: 28,
                                height: 28,
                                border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
                                bgcolor: isDark ? alpha("#1e293b", 0.6) : "#ffffff",
                                "&:hover": { bgcolor: isDark ? "#334155" : "#f1f5f9" },
                            }}
                        >
                            <ChevronLeft sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleNext}
                            sx={{
                                width: 28,
                                height: 28,
                                border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
                                bgcolor: isDark ? alpha("#1e293b", 0.6) : "#ffffff",
                                "&:hover": { bgcolor: isDark ? "#334155" : "#f1f5f9" },
                            }}
                        >
                            <ChevronRight sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Stack>
                )}
            </Box>

            {/* Card Stack Deck Container */}
            <Box
                sx={{
                    position: "relative",
                    pb: totalCards > 1 ? (totalCards > 2 ? 3.5 : 2) : 0,
                    perspective: 1000,
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                }}
            >
                {/* Background Cards (Stacked underneath for depth effect) */}
                {backgroundCards.map((bgCard, idx) => {
                    const bgPriority =
                        priorityConfig[bgCard.task.priority?.toLowerCase()] || priorityConfig.medium;
                    return (
                        <Paper
                            key={`bg-card-${bgCard.task.id}-${idx}`}
                            elevation={0}
                            sx={{
                                position: "absolute",
                                top: bgCard.offsetY,
                                left: 0,
                                right: 0,
                                p: 2,
                                borderRadius: 3,
                                border: `1px solid ${isDark ? alpha("#334155", 0.4) : alpha("#e2e8f0", 0.6)}`,
                                background: isDark ? alpha("#1e293b", 0.9) : alpha("#ffffff", 0.9),
                                // backdropFilter: "blur(12px)",
                                // WebkitBackdropFilter: "blur(12px)",
                                transform: `scale(${bgCard.scale})`,
                                transformOrigin: "top center",
                                opacity: bgCard.opacity,
                                zIndex: bgCard.zIndex,
                                pointerEvents: "none",
                                width: "100%",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                                boxShadow: isDark
                                    ? "0 4px 12px rgba(0,0,0,0.2)"
                                    : "0 4px 12px rgba(15,23,42,0.04)",
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 4,
                                    height: 32,
                                    borderRadius: 2,
                                    bgcolor: bgPriority.color,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: "text.secondary",
                                    fontSize: "0.875rem",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    flex: 1,
                                }}
                            >
                                {bgCard.task.title}
                            </Typography>
                        </Paper>
                    );
                })}

                {/* Top Active Card with AnimatePresence transition */}
                <AnimatePresence initial={false} custom={direction} mode="wait">
                    <motion.div
                        key={currentTask.id}
                        custom={direction}
                        variants={cardVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        style={{
                            position: "relative",
                            zIndex: 3,
                            width: "100%",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                        }}
                    >
                        <Paper
                            elevation={0}
                            onClick={handleCardClick}
                            sx={{
                                p: 2.25,
                                borderRadius: 3,
                                border: `1.5px solid ${isDark ? alpha(priority.color, 0.4) : alpha(priority.color, 0.3)
                                    }`,
                                background: isDark
                                    ? `linear-gradient(135deg, ${alpha("#1e293b", 0.99)} 0%, ${alpha(
                                        "#0f172a",
                                        0.99
                                    )} 100%)`
                                    : alpha("#ffffff", 0.8),
                                // backdropFilter: "blur(16px)",
                                // WebkitBackdropFilter: "blur(16px)",
                                width: "100%",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                                boxShadow: isDark
                                    ? `0 10px 25px -5px ${alpha("#000000", 0.5)}, 0 0 15px ${alpha(
                                        priority.color,
                                        0.15
                                    )}`
                                    : `0 10px 25px -5px ${alpha("#0f172a", 0.08)}, 0 0 15px ${alpha(
                                        priority.color,
                                        0.08
                                    )}`,
                                cursor: totalCards > 1 ? "pointer" : "default",
                                transition: "border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
                                "&:hover": {
                                    borderColor: priority.color,
                                    boxShadow: isDark
                                        ? `0 12px 30px -5px ${alpha("#000000", 0.6)}, 0 0 20px ${alpha(
                                            priority.color,
                                            0.25
                                        )}`
                                        : `0 12px 30px -5px ${alpha("#0f172a", 0.12)}, 0 0 20px ${alpha(
                                            priority.color,
                                            0.15
                                        )}`,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 2,
                                }}
                            >
                                {/* Priority Color Indicator Line */}
                                <Box
                                    sx={{
                                        width: 5,
                                        minHeight: 44,
                                        borderRadius: 2.5,
                                        bgcolor: priority.color,
                                        flexShrink: 0,
                                        boxShadow: `0 0 8px ${priority.color}`,
                                    }}
                                />

                                {/* Main Card Content */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justify: "space-between",
                                            gap: 1,
                                            mb: 0.75,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 700,
                                                color: "text.primary",
                                                fontSize: "0.95rem",
                                                lineHeight: 1.3,
                                                flex: 1,
                                            }}
                                        >
                                            {currentTask.title}
                                        </Typography>

                                        {/* Priority Chip */}
                                        <Chip
                                            size="small"
                                            icon={priority.icon}
                                            label={priority.label}
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "0.68rem",
                                                height: 22,
                                                bgcolor: priority.bg,
                                                color: priority.color,
                                                borderRadius: 1.5,
                                                "& .MuiChip-icon": {
                                                    fontSize: 14,
                                                    color: priority.color,
                                                },
                                            }}
                                        />
                                    </Box>

                                    {/* Task Metadata Row */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            flexWrap: "wrap",
                                            mt: 1,
                                        }}
                                    >
                                        {/* Status */}
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            {getStatusIcon(currentTask.status)}
                                            <Typography
                                                variant="caption"
                                                sx={{ color: status.color, fontWeight: 600 }}
                                            >
                                                {status.label}
                                            </Typography>
                                        </Stack>

                                        {/* Due Date */}
                                        {currentTask.due_date && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                        new Date(currentTask.due_date) < new Date()
                                                            ? "#ef4444"
                                                            : "text.secondary",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                📅 {formatDate(currentTask.due_date)}
                                            </Typography>
                                        )}

                                        {/* Assignee */}
                                        {(currentTask.assigned_to || currentTask.assigned_to_name) && (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Person sx={{ fontSize: 13, color: "text.secondary" }} />
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    {typeof currentTask.assigned_to === "object"
                                                        ? currentTask.assigned_to?.name
                                                        : currentTask.assigned_to_name || currentTask.assigned_to}
                                                </Typography>
                                            </Stack>
                                        )}

                                        {/* Time Spent */}
                                        {currentTask.actual_hours && currentTask.actual_hours !== "0.00" && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                ⏱ {getTimeSpent(currentTask.actual_hours)}
                                            </Typography>
                                        )}

                                        {/* Parent Task Progress Badge */}
                                        {currentTask.progress_percentage !== undefined &&
                                            currentTask.is_parent_task === 1 && (
                                                <Chip
                                                    size="small"
                                                    label={`${currentTask.progress_percentage}%`}
                                                    sx={{
                                                        height: 18,
                                                        fontSize: "0.6rem",
                                                        fontWeight: 700,
                                                        bgcolor: alpha("#10b981", 0.12),
                                                        color: "#10b981",
                                                    }}
                                                />
                                            )}
                                    </Box>

                                    {/* Progress Bar for Parent Task */}
                                    {currentTask.progress_percentage !== undefined &&
                                        currentTask.is_parent_task === 1 && (
                                            <Box sx={{ mt: 1.25 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={currentTask.progress_percentage}
                                                    sx={{
                                                        height: 4,
                                                        borderRadius: 2,
                                                        bgcolor: isDark ? "#1e293b" : "#e2e8f0",
                                                        "& .MuiLinearProgress-bar": {
                                                            bgcolor:
                                                                currentTask.progress_percentage === 100
                                                                    ? "#10b981"
                                                                    : "#3b82f6",
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                </Box>

                                {/* Expand Description Button */}
                                {currentTask.description && (
                                    <Tooltip title={isExpanded ? "Collapse info" : "Expand info"}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => toggleExpand(e, currentTask.id)}
                                            sx={{
                                                mt: -0.5,
                                                bgcolor: isDark
                                                    ? alpha("#334155", 0.4)
                                                    : alpha("#f1f5f9", 0.8),
                                                "&:hover": {
                                                    bgcolor: isDark ? "#334155" : "#e2e8f0",
                                                },
                                            }}
                                        >
                                            {isExpanded ? (
                                                <ExpandLess sx={{ fontSize: 18 }} />
                                            ) : (
                                                <ExpandMore sx={{ fontSize: 18 }} />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {/* Expanded Description Accordion */}
                            <AnimatePresence>
                                {isExpanded && currentTask.description && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.2 }}
                                        style={{ overflow: "hidden" }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Box
                                            sx={{
                                                mt: 2,
                                                pt: 2,
                                                borderTop: `1px dashed ${isDark ? "#334155" : "#e2e8f0"
                                                    }`,
                                                pl: 2.5,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "text.secondary",
                                                    fontSize: "0.825rem",
                                                    lineHeight: 1.6,
                                                    wordBreak: "break-word",
                                                    overflowWrap: "anywhere",
                                                }}
                                            >
                                                {currentTask.description}
                                            </Typography>

                                            {/* Labels */}
                                            {currentTask.labels && currentTask.labels.length > 0 && (
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{ mt: 1.5, flexWrap: "wrap" }}
                                                >
                                                    {currentTask.labels.map((label, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={label}
                                                            size="small"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: "0.625rem",
                                                                bgcolor: isDark
                                                                    ? alpha("#3b82f6", 0.12)
                                                                    : alpha("#3b82f6", 0.08),
                                                                color: isDark ? "#60a5fa" : "#3b82f6",
                                                                border: `1px solid ${isDark
                                                                    ? alpha("#3b82f6", 0.25)
                                                                    : alpha("#3b82f6", 0.15)
                                                                    }`,
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>
                                            )}
                                        </Box>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </Paper>
                    </motion.div>
                </AnimatePresence>
            </Box>

            {/* Bottom Dots Indicator for Stacked Deck Navigation */}
            {totalCards > 1 && (
                <Stack
                    direction="row"
                    spacing={0.75}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mt: 1.5 }}
                >
                    {priorityTasks.map((_, idx) => (
                        <Box
                            key={idx}
                            onClick={(e) => {
                                e.stopPropagation();
                                setDirection(idx > activeIndex ? 1 : -1);
                                setActiveIndex(idx);
                                setExpandedTask(null);
                            }}
                            sx={{
                                width: idx === activeIndex ? 20 : 6,
                                height: 6,
                                borderRadius: 3,
                                bgcolor:
                                    idx === activeIndex
                                        ? priority.color
                                        : isDark
                                            ? "#334155"
                                            : "#cbd5e1",
                                cursor: "pointer",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                    bgcolor: idx === activeIndex ? priority.color : "#94a3b8",
                                },
                            }}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export default TopPriorityTasks;